import type { IRowNode } from 'ag-grid-community';
import Big from 'big.js';
import { isNil, some } from 'lodash-es';
import { useCallback } from 'react';
import { selectOptionFilter, selectStrikeFilter } from '../Filter/selectors';

import { useAppStateSelector } from '@src/providers/AppStateProvider';
import { ALL_EXPIRIES_ITEM, type OptionChainDataRow } from '../types';

/**
 * Filters the rows, is called for each rowNode.
 */
export function useOptionChainRowFilter({
  crossStrikeByExpiry,
  expiryToStrikePriceToIndex,
  panelID,
}: {
  crossStrikeByExpiry: Map<string, string | undefined>;
  expiryToStrikePriceToIndex: Map<string, Map<string, number>>;
  panelID: string;
}) {
  const strikeFilter = useAppStateSelector(state => selectStrikeFilter(state, panelID));
  const optionFilter = useAppStateSelector(state => selectOptionFilter(state, panelID));

  return useCallback(
    (row: Pick<IRowNode<OptionChainDataRow>, 'data'>) => {
      const { data } = row;
      if (!data) {
        return false;
      }

      if (data.Currency !== optionFilter.currency) {
        return false;
      }
      if (data.Expiration !== optionFilter.expiry && optionFilter.expiry !== ALL_EXPIRIES_ITEM) {
        return false;
      }
      if (data.Market !== optionFilter.market) {
        return false;
      }
      // If underlyingCode is set on filter, but not on data, compare underlying with data currency
      if (optionFilter.underlyingCode && !data.UnderlyingCode && optionFilter.underlyingCode !== data.Currency) {
        return false;
      }
      // if underlyingCode is available on data, compare it to filter
      if (data.UnderlyingCode && optionFilter.underlyingCode !== data.UnderlyingCode) {
        return false;
      }

      if (strikeFilter.strikePrice) {
        /**
         * Filter based on if the StrikePrice of the row is between range (from -> to).
         */

        const minStrike = strikeFilter.strikePrice.from;
        const maxStrike = strikeFilter.strikePrice.to;

        return (
          (!maxStrike || Big(data.StrikePrice).lte(maxStrike)) && (!minStrike || Big(data.StrikePrice).gte(minStrike))
        );
      } else if (strikeFilter.atm) {
        /**
         * Filter based on numeric proximity to crossStrike (crossStrike is the closest strike to the underlying amount).
         */

        const { Expiration, StrikePrice } = data;
        const currentRowStrikeIndex = expiryToStrikePriceToIndex.get(Expiration)?.get(StrikePrice);

        // Get the crossStrike for the Expiration of the row
        const crossStrike = crossStrikeByExpiry.get(data.Expiration);

        // Get the index of the crossStrike of all StrikePrices for the expiry.
        const crossStrikeIndex =
          (!isNil(crossStrike) ? expiryToStrikePriceToIndex.get(Expiration)?.get(crossStrike) : -1) ?? -1;

        if (crossStrikeIndex === -1) {
          // If we are yet to have a crossStrike and index of it, show all rows.
          return true;
        }

        if (!isNil(currentRowStrikeIndex)) {
          // Grab upper and lower index given crossStrike index
          const numberOfStrikePrices = strikeFilter.atm;
          const upperBoundIndex = crossStrikeIndex + numberOfStrikePrices;
          const lowerBoundIndex = Math.max(crossStrikeIndex - numberOfStrikePrices, 0);
          return Big(currentRowStrikeIndex).lte(upperBoundIndex) && Big(currentRowStrikeIndex).gte(lowerBoundIndex);
        }
      } else if (strikeFilter.delta) {
        /**
         * Filter based on the absolute value of delta (of put/call) is within specified range.
         */

        const callDelta = data.Call?.Delta;
        const putDelta = data.Put?.Delta;

        // If no delta data exists, do not show the row.
        if (!callDelta && !putDelta) {
          return false;
        }

        const { from, to } = strikeFilter.delta;

        return some([callDelta, putDelta], d => {
          if (isNil(d)) {
            return false;
          }
          const absBigDelta = Big(d).abs();

          if (from && absBigDelta.lt(from)) {
            return false;
          }
          if (to && absBigDelta.gt(to)) {
            return false;
          }

          return true;
        });
      }

      // If no filter is specified
      return true;
    },
    /**
     * Keep this list of dependencies as stable as possible, since each new reference of `rowFilter` will trigger
     * all blotter rows to be filtered.
     */
    [
      crossStrikeByExpiry,
      optionFilter.expiry,
      optionFilter.market,
      optionFilter.currency,
      optionFilter.underlyingCode,
      strikeFilter.atm,
      strikeFilter.delta,
      strikeFilter.strikePrice,
      expiryToStrikePriceToIndex,
    ]
  );
}
